import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef
} from 'react';
import { connect } from 'react-redux';
import MaterialTable, { MTableToolbar } from '@material-table/core';
import flowRight from 'lodash.flowright';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/styles';

import { mapTableOptions, TABLE_OPTIONS } from '../../../consts/tableOptions';
import useTableLocalization from '../../../utils/useTableLocalization';
import AddButton from '../../../partials/tableCustomButtons/AddButton';
import tableColumns from './tableColumns';
import { isDefined, isUndefined } from '../../../utils/isDefined';
import useLocalTableStore from '../../../utils/useLocalTableStore';
import { actions as reduxActions } from '../reducers';
import customTableIcons from '../../../partials/tableIcons/customTableIcons';
import RangesTableHeader from './RangesTableHeader';
import { setRange } from '../../../modals/editRange/actions/setRange';
import can from '../../../utils/can';
import createSubject from '../../../utils/createSubject';
import { openRangesMultiedit } from '../../../modals/numberRangeMultiedit/actions/setRanges';
import fetchRangesPaginate from '../actions/fetchRangesPaginate';
import fetchLookupOperators from '../actions/fetchLookupOperators';
import exportNumberRangesCsv from '../actions/exportNumberRangesCsv';
import styles from '../styles';
import CustomIconButton from '../../../partials/tableCustomButtons/CustomIconButton';
import RefreshButton from '../../../partials/tableCustomButtons/RefreshButton';
import { actions as moveNumberRangeActions } from '../../../modals/moveNumberRange/reducers';
import FilterButton from '../../../partials/tableCustomButtons/FilterButton';
import { translate } from '../../../i18n/I18nProvider';

export function NumberRangeTableBE({
  intl,
  client,
  isLoading,
  columns,
  setColumns,
  lockUserAction,
  clientName,
  customJobs,
  refresh,
  setRefresh,
  setRange,
  connectClientId,
  openRangesMultiedit,
  classes,
  moveNumberRange
}) {
  const [getServiceOperator, setGetServiceOperator] = useState(null);
  const [getConnectRanges, setGetConnectRanges] = useState(null);
  const fetchStateRef = useRef({ cachedResult: null, skipFetch: false });
  const prevServiceOperatorRef = useRef(null);
  const { localization, language } = useTableLocalization();
  const [filtering, setFiltering] = useState(false);

  const tableDefaultColumns = useMemo(() => {
    return tableColumns({ clientId: client, lockUserAction, isBeTable: true });
  }, [client, lockUserAction]);

  const tableRef = useRef();

  const {
    pageSize,
    onChangeRowsPerPage,
    onChangeColumnHidden
  } = useLocalTableStore({
    tableName: 'NumberRanges-Admin-2.1',
    setColumns,
    tableDefaultColumns,
    language
  });

  useEffect(() => {
    const telepoRanges = columns.find(
      el =>
        el.title ===
        intl.formatMessage({
          id: 'NUMBER_RANGE.DOMAIN_RANGE'
        })
    );

    if (isDefined(telepoRanges) && !telepoRanges.hidden !== getConnectRanges) {
      setGetConnectRanges(!telepoRanges.hidden);
    }

    const serviceOperator = columns.find(
      el =>
        el.title ===
        intl.formatMessage({
          id: 'OPERATOR'
        })
    );

    if (
      isDefined(serviceOperator) &&
      !serviceOperator.hidden !== getServiceOperator
    ) {
      setGetServiceOperator(!serviceOperator.hidden);
    }

    // eslint-disable-next-line
  }, [columns, getConnectRanges]);

  useEffect(() => {
    if (prevServiceOperatorRef.current === null) {
      prevServiceOperatorRef.current = getServiceOperator;
      return;
    }
    prevServiceOperatorRef.current = getServiceOperator;
    if (fetchStateRef.current.cachedResult) {
      fetchStateRef.current.skipFetch = true;
    }
    if (tableRef.current) {
      tableRef.current.onQueryChange();
    }
  }, [getServiceOperator]);

  useEffect(() => {
    if (refresh) {
      tableRef.current.onQueryChange();
      setRefresh(false);
    }
    // eslint-disable-next-line
  }, [refresh]);

  const fetchData = useCallback(
    async query => {
      let result;
      if (
        fetchStateRef.current.skipFetch &&
        fetchStateRef.current.cachedResult
      ) {
        fetchStateRef.current.skipFetch = false;
        result = fetchStateRef.current.cachedResult;
      } else {
        result = await fetchRangesPaginate({
          query,
          client,
          getConnectRanges
        });
        fetchStateRef.current.cachedResult = result;
      }

      if (getServiceOperator) {
        try {
          const rangesWithLookup = await fetchLookupOperators(result.data);
          return { ...result, data: rangesWithLookup };
        } catch {
          return result;
        }
      }

      return result;
    },
    // eslint-disable-next-line
    [client, getServiceOperator, getConnectRanges]
  );

  const actions = useMemo(
    () => {
      let buttons = [
        {
          icon: () => <FilterButton />,
          onClick: () => {
            setFiltering(!filtering);
          },
          isFreeAction: true,
          tooltip: intl.formatMessage({ id: 'BUTTON.FILTER' })
        },
        {
          icon: () => <AddButton buttonText="BUTTON.ADD" />,
          onClick: () =>
            setRange({
              range: {},
              resetValues: {
                id: client,
                getServiceOperator,
                getConnectRanges,
                connectClientId
              },
              isEdit: false,
              isBeTable: true
            }),
          isFreeAction: true,
          hidden:
            !can(
              'create',
              createSubject('ExternalNumberRange', {
                client_id: Number(client)
              })
            ) || isUndefined(client)
        },
        {
          tooltip: intl.formatMessage({ id: 'BUTTON.MULTIEDIT' }),
          icon: () => <CustomIconButton iconClassName="far fa-edit" />,
          onClick: (evt, data) =>
            openRangesMultiedit({
              ranges: data,
              resetValues: {
                id: client,
                getServiceOperator,
                getConnectRanges,
                connectClientId
              },
              isBeTable: true
            })
        },
        {
          tooltip: intl.formatMessage({
            id: 'NUMBER_RANGES.MULTI_MOVE_RANGES'
          }),
          icon: () => (
            <CustomIconButton iconClassName="far fa-arrow-circle-right" />
          ),
          onClick: (evt, data) =>
            moveNumberRange({
              ranges: data
            })
        },
        {
          icon: () => <RefreshButton tableHeader={true} />,
          onClick: () => setRefresh(true),
          isFreeAction: true
        }
      ];

      return buttons;
    },
    // eslint-disable-next-line
    [customJobs, client, getServiceOperator, getConnectRanges]
  );

  const tableOptions = useMemo(() => {
    return {
      ...mapTableOptions({ tableName: 'numberRanges', clientName }),
      pageSize,
      filtering,
      selection: true,
      exportMenu: [
        {
          label: `${translate('TABLE.EXPORT_AS')} CSV`,
          exportFunc: () =>
            exportNumberRangesCsv({
              dataManager: tableRef.current.dataManager,
              client,
              getConnectRanges,
              clientName
            })
        }
      ]
    };
  }, [clientName, pageSize, filtering]);

  return (
    <MaterialTable
      components={{
        Toolbar: props => (
          <MTableToolbar
            classes={{
              title: classes.toolbarTitle,
              spacer: classes.toolbarSpacer,
              actions: classes.toolbarActions,
              highlight: classes.toolbarHighlight
            }}
            {...props}
          />
        )
      }}
      title={<RangesTableHeader showSelector={isDefined(client)} />}
      icons={customTableIcons}
      actions={actions}
      columns={columns}
      data={fetchData}
      options={tableOptions}
      localization={localization}
      tableRef={tableRef}
      onChangeColumnHidden={onChangeColumnHidden}
    />
  );
}

const mapStatesToProps = ({ externalNoRanges, auth, selectClient, loader }) => {
  return {
    ...externalNoRanges,
    isLoading: loader.isSpinVisible,
    clientName: selectClient.selectedClient.name,
    connectClientId: selectClient.selectedClient.connect30_domain,
    userConnectId: auth.user.connect30_user_id,
    customJobs: auth.customJobs
  };
};

const mapDispatchToProps = {
  setColumns: reduxActions.setColumns,
  setRefresh: reduxActions.setRefresh,
  resetExternalNoRanges: reduxActions.resetExternalNoRanges,
  setRange,
  openRangesMultiedit,
  moveNumberRange: moveNumberRangeActions.openMoveRange
};

export default flowRight(
  withRouter,
  injectIntl,
  connect(mapStatesToProps, mapDispatchToProps),
  withStyles(styles)
)(NumberRangeTableBE);
